import UiLanguageSelect from "../components/UiLanguageSelect";
import { useTranslation } from "react-i18next";
// src/pages/VerifyEmail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { verifyEmail } from "../api/auth";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type Status = "idle" | "loading" | "success" | "error";

type VerifyOutcome =
  | { kind: "local"; code: "invalidLink" | "success" | "failed" | "invalidToken" | "invalidOrExpiredToken" | "expiredLink" }
  | { kind: "raw"; value: React.ReactNode };

function getErrorOutcome(err: unknown): VerifyOutcome {
  const anyErr = err as any;
  const detail = anyErr?.response?.data?.detail;
  if (anyErr?.response?.status === 400) {
    if (detail === "Invalid verification token.") return { kind: "local", code: "invalidToken" };
    if (detail === "Invalid or expired verification token.") return { kind: "local", code: "invalidOrExpiredToken" };
    if (detail === "Verification link has expired.") return { kind: "local", code: "expiredLink" };
  }
  const value = detail || anyErr?.message;
  return value ? { kind: "raw", value } : { kind: "local", code: "failed" };
}

export default function VerifyEmail() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<VerifyOutcome | null>(null);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setStatus("error");
      setMessage({ kind: "local", code: "invalidLink" });
      return;
    }

    (async () => {
      try {
        setStatus("loading");
        setMessage(null);
        await verifyEmail(token);
        if (cancelled) return;
        setStatus("success");
        setMessage({ kind: "local", code: "success" });
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(getErrorOutcome(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const messageText = message?.kind === "raw" ? message.value
    : message?.code === "invalidLink" ? t("verifyEmail.invalidLink")
    : message?.code === "success" ? t("verifyEmail.success")
    : message?.code === "invalidToken" ? t("verifyEmail.invalidToken")
    : message?.code === "invalidOrExpiredToken" ? t("verifyEmail.invalidOrExpiredToken")
    : message?.code === "expiredLink" ? t("verifyEmail.expiredLink")
    : message ? t("verifyEmail.failed") : "";

  const isLoading = status === "loading";

  const alertCls = [
    "rounded-2xl px-4 py-3 text-[13px] text-center border",
    status === "success"
      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
      : "bg-rose-50 border-rose-200 text-rose-700",
  ].join(" ");

  return (
    <div className={`min-h-[60vh] bg-gray-50 ${A.body} flex items-center justify-center px-4 py-10`}>
      <div className={`${A.card} w-full max-w-md p-6`}>
        <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
        <div className="text-center">
          <div className={A.title}>{t("verifyEmail.title")}</div>
          <div className={`mt-1 ${A.label}`}>{t("verifyEmail.explanation")}</div>
        </div>

        <div className="mt-5">
          {isLoading ? (
            <div className="text-center text-[13px] text-gray-600 py-4">
              {t("verifyEmail.verifying")}
            </div>
          ) : (
            <div className={alertCls} aria-live="polite">
              {messageText}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            type="button"
            className="w-full justify-center"
            onClick={() => navigate("/login")}
            disabled={isLoading}
          >
            {t("verifyEmail.goToLogin")}
          </Button>

          <div className={`text-center ${A.label}`}>
            {t("verifyEmail.needAccount")}{" "}
            <Link to="/register" className="text-appleblue hover:underline">
              {t("login.register")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
